import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';

@Entity()
export class TaskqueueOdooEntity extends MongoEntity {
  static override _class = 'TaskqueueOdooEntity';
  static override _collectionName = 'TaskqueueOdoo';

  @Field(() => String) odooTicketID: Maybe<string>;
  @Field(() => String) subject: Maybe<string>;
  @Field(() => String) from: Maybe<string>;
  @Field(() => Date) createdAt: Maybe<Date>;
  @Field(() => Boolean) hasTags: Maybe<boolean>;
  @Field(() => Boolean) hasPriority: Maybe<boolean>;
  @Field(() => Boolean) hasContact: Maybe<boolean>;
  @Field(() => [String]) comments: Maybe<string[]>; // Array to store user comments

  static override async beforeCreate(entity: TaskqueueOdooEntity) {
    await super.beforeCreate(entity);
    entity.createdAt = new Date();
  }
}

export async function setTaskQueueTagCompleted(ticketId: string) {
  const collection = await getCollection<TaskqueueOdooEntity>(TaskqueueOdooEntity._class);
  // insert or update each ticket
  // ? does not insert a ticket if it does not exist

  // const existingTicket = await collection.findOne({ odooTicketID: ticketId });
  // if (existingTicket) {
  await collection.updateOne(
    { odooTicketID: ticketId },
    {
      $set: {
        hasTags: true,
      },
    },
  );
  // }
}

export async function setTaskQueuePriorityCompleted(ticketId: string) {
  const collection = await getRawCollection<TaskqueueOdooEntity>(TaskqueueOdooEntity._collectionName);
  // insert or update each ticket
  // ? this does not insert a ticket

  // const existingTicket = await collection.findOne({ odooTicketID: ticketId });
  // if (existingTicket) {
  await collection.updateOne(
    { odooTicketID: ticketId },
    {
      $set: {
        hasPriority: true,
      },
    },
  );
  // }
}

export async function setTaskQueueContactCompleted(ticketId: string) {
  const collection = await getCollection<TaskqueueOdooEntity>(TaskqueueOdooEntity._class);
  // insert or update each ticket
  const existingTicket = await collection.findOne({ odooTicketID: ticketId });
  if (existingTicket) {
    await collection.updateOne(
      { odooTicketID: ticketId },
      {
        $set: {
          hasContact: true,
        },
      },
    );
  }
}

export async function replaceCompleteOdooTaskqueueTags(
  ticketList: Array<{ tag_ids?: string[]; id: string; name: string; partner_email: string }>,
) {
  if (!ticketList.length) return;

  const collection = await getRawCollection<TaskqueueOdooEntity>(TaskqueueOdooEntity._collectionName);

  const bulk = collection.initializeUnorderedBulkOp();
  for (const ticket of ticketList) {
    let hasTagsCheck = ticket.tag_ids && ticket.tag_ids.length > 0;
    // insert or update each ticket
    const ticketId = ticket.id.toString();
    if (!ticketId) {
      logger.warn('Skipping ticket without odooTicketID:', ticket);
      continue;
    }

    bulk
      .find({ odooTicketID: ticketId })
      .upsert()
      .update({
        $set: {
          subject: ticket.name,
          from: ticket.partner_email,
          hasTags: hasTagsCheck,
        },
        $setOnInsert: {
          odooTicketID: ticketId,
          hasContact: true,
          _id: A.getGloballyUniqueID(),
          _class: TaskqueueOdooEntity._class,
          createdAt: new Date(),
        },
      });

    // const existingTicket = await collection.findOne({ odooTicketID: ticketId });
    // if (existingTicket) {
    //   await collection.updateOne(
    //     { odooTicketID: ticketId },
    //     {
    //       $set: {
    //         subject: ticket.name,
    //         from: ticket.partner_email,
    //         hasTags: hasTagsCheck,
    //       },
    //     },
    //   );
    // } else {
    //   const newTicket = new TaskqueueOdooEntity({
    //     odooTicketID: ticketId,
    //     subject: ticket.name,
    //     from: ticket.partner_email,
    //     hasTags: hasTagsCheck,
    //     hasContact: true,
    //   });
    //   await newTicket.create();
    // }
  }

  await bulk.execute();
}

export async function replaceCompleteOdooTaskqueuePriority(
  ticketList: Array<{ tag_ids?: string[]; id: string; name: string; partner_email: string }>,
) {
  if (!ticketList.length) return;

  const collection = await getRawCollection<TaskqueueOdooEntity>(TaskqueueOdooEntity._collectionName);

  const bulk = collection.initializeUnorderedBulkOp();
  for (const ticket of ticketList) {
    let hasTagsCheck = ticket.tag_ids && ticket.tag_ids.length > 0;
    const ticketId = ticket.id.toString();
    if (!ticketId) {
      logger.warn('Skipping ticket without odooTicketID:', ticket);
      continue;
    }

    bulk
      .find({ odooTicketID: ticketId })
      .upsert()
      .update({
        $set: { hasPriority: false, hasTags: hasTagsCheck },
        $setOnInsert: {
          odooTicketID: ticketId,
          hasContact: true,
          subject: ticket.name,
          from: ticket.partner_email,
          _id: A.getGloballyUniqueID(),
          _class: TaskqueueOdooEntity._class,
          createdAt: new Date(),
        },
      });

    // const existingTicket = await collection.findOne({ odooTicketID: ticketId });
    // if (existingTicket) {
    //   await collection.updateOne(
    //     { odooTicketID: ticketId },
    //     {
    //       $set: {
    //         hasPriority: false,
    //         hasTags: hasTagsCheck,
    //       },
    //     },
    //   );
    // } else {
    //   const newTicket = new TaskqueueOdooEntity({
    //     odooTicketID: ticketId,
    //     hasPriority: false,
    //     hasTags: hasTagsCheck,
    //     hasContact: true,
    //     subject: ticket.name,
    //     from: ticket.partner_email,
    //   });
    //   await newTicket.create();
    // }
  }

  await bulk.execute();
}

export async function replaceCompleteOdooTaskqueueContacts(ticketList: Record<string, any>[]) {
  const collection = await getCollection<TaskqueueOdooEntity>(TaskqueueOdooEntity._class);
  for (const ticket of ticketList) {
    const ticketId = ticket.id.toString();
    let hasTagsCheck = ticket.tag_ids && ticket.tag_ids.length > 0;
    if (!ticketId) {
      logger.warn('Skipping ticket without odooTicketID:', ticket);
      continue;
    }
    const existingTicket = await collection.findOne({ odooTicketID: ticketId });
    if (existingTicket) {
      await collection.updateOne(
        { odooTicketID: ticketId },
        {
          $set: {
            hasContact: false,
            hasTags: hasTagsCheck,
          },
        },
      );
    } else {
      const newTicket = new TaskqueueOdooEntity({
        odooTicketID: ticketId,
        hasContact: false,
        hasTags: hasTagsCheck,
      });
      await newTicket.create();
    }
  }
}
